import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, shareReplay, tap, finalize } from 'rxjs';

import { environment } from '../../../environments/environment.development';
import { DeviceIdentityDto } from '../api/models';

const DEVICE_ID_KEY = 'zaklepuj_device_id';
const DEVICE_TOKEN_KEY = 'zaklepuj_device_token';

/**
 * Serwerowa tożsamość anonimowego urządzenia (deviceId + podpisany token).
 *
 * Backend wydaje parę przez POST /privacy/device-token. Klient zapisuje ją w
 * localStorage i wysyła `X-Device-Token` (+ `X-Device-Id`) na endpointy Privacy
 * Center / consents (patrz DeviceHeaderInterceptor). Token jest podpisany przez
 * serwer, więc anonimowy gość nie podszyje się pod cudze dane (zamiennik dla
 * podrabialnego, surowego X-Device-Id).
 *
 * NIE dotyczy czatu — wątki gościa identyfikuje osobny `token` (threadId:token).
 */
@Injectable({ providedIn: 'root' })
export class DeviceTokenService {
  private readonly http = inject(HttpClient);

  private identity: DeviceIdentityDto | null = this.loadFromStorage();
  private inFlight$: Observable<DeviceIdentityDto> | null = null;

  /**
   * Zwraca tożsamość urządzenia, w razie potrzeby wydając ją jednorazowo przez API.
   * Współbieżne wywołania współdzielą jedno żądanie.
   */
  ensureDeviceIdentity(): Observable<DeviceIdentityDto> {
    if (this.identity) {
      return of(this.identity);
    }

    if (!this.inFlight$) {
      this.inFlight$ = this.http
        .post<DeviceIdentityDto>(`${environment.apiUrl}/privacy/device-token`, {})
        .pipe(
          tap((identity) => {
            this.identity = identity;
            this.saveToStorage(identity);
          }),
          finalize(() => {
            this.inFlight$ = null;
          }),
          shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    return this.inFlight$;
  }

  getStoredIdentity(): DeviceIdentityDto | null {
    return this.identity;
  }

  private loadFromStorage(): DeviceIdentityDto | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    try {
      const deviceId = localStorage.getItem(DEVICE_ID_KEY);
      const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY);
      return deviceId && deviceToken ? { deviceId, deviceToken } : null;
    } catch {
      return null;
    }
  }

  private saveToStorage(identity: DeviceIdentityDto): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    try {
      localStorage.setItem(DEVICE_ID_KEY, identity.deviceId);
      localStorage.setItem(DEVICE_TOKEN_KEY, identity.deviceToken);
    } catch {
      console.warn('[DeviceTokenService] Failed to persist device identity');
    }
  }
}
